import React from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import { Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import { LengthUnit } from "@services/demosService";
import {
  convertLength,
  roundLengthForDisplay,
  roundLengthForStorage,
} from "@src/utils/convert";

/**
 * Editor for a device's header-width history.
 *
 * Width is a timeline rather than a single number: a machine's header can be
 * swapped mid-season, and harvested area, kg/ha and the swath ribbon are all
 * measured at the width that was actually fitted on the day in question.
 *
 * Two things are deliberate here:
 *
 *  - The FIRST row's date is not editable. That segment governs everything
 *    before the next one starts, so a date on it would be meaningless — it
 *    reads "from the start". Its WIDTH is editable, and that is the correction
 *    for a device that was auto-provisioned at the 6.7 m default it never had.
 *  - The whole list is submitted at once. Validity is a property of the set
 *    (ordered, no two rows at the same instant), so there is no per-row save.
 *
 * Widths are displayed and typed in the viewer's length unit; `onChange`
 * always emits metres, which is what the API stores.
 */

export type WidthRow = {
  /** ISO instant. Ignored for the first row, which governs all earlier time. */
  effectiveFrom: string;
  /** Metres — always, whatever unit is on screen. */
  widthMeters: number;
};

const Block = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #d14343;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
`;

const AddBtn = styled.button`
  background: none;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #494c5b;
  cursor: pointer;
  font-size: 13px;
  padding: 8px;
  width: 100%;
`;

const Field = styled.input`
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  padding: 6px 8px;
  width: 100%;
`;

const Warning = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  color: #9a3412;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;
  padding: 8px 10px;
`;

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" with no zone or seconds. */
export const toLocalInput = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

/**
 * True when the submitted list restates a day the user has no reason to expect
 * to change.
 *
 * Two edits are routine and pass silently:
 *
 *  - appending a new dated row ("a different header was fitted"), which by
 *    construction touches nothing before that date;
 *  - correcting the width that is in force NOW, which is what the plain width
 *    field does — the "this machine was never 6.7 m" fix. It does restate the
 *    current segment, and the field's own hint says so.
 *
 * Everything else — a date moved, a line removed, the width of a segment that
 * has already been superseded — is a restatement of closed days and is
 * confirmed before it is sent.
 */
export const restatesHistory = (
  original: WidthRow[],
  next: WidthRow[]
): boolean => {
  if (next.length < original.length) return true;
  const appended = next.length > original.length;
  for (let i = 0; i < original.length; i++) {
    if (next[i] == null) return true;
    // The first row's date is not shown and not editable, so ignore it.
    if (i > 0 && next[i].effectiveFrom !== original[i].effectiveFrom) return true;
    if (next[i].widthMeters !== original[i].widthMeters) {
      const stillCurrent = i === original.length - 1 && !appended;
      if (!stillCurrent) return true;
    }
  }
  return false;
};

type Props = {
  rows: WidthRow[];
  onChange: (rows: WidthRow[]) => void;
  lengthUnit: LengthUnit;
  unitLabel: string;
  labels: {
    fromTheStart: string;
    changedOn: string;
    addEarlier: string;
    addNew?: string;
    remove: string;
    restatesWarning: string;
  };
  disabled?: boolean;
};

const WidthHistoryEditor: React.FC<Props> = ({
  rows,
  onChange,
  lengthUnit,
  unitLabel,
  labels,
  disabled,
}) => {
  const display = (meters: number) =>
    roundLengthForDisplay(convertLength(meters, LengthUnit.Meter, lengthUnit));

  const setWidth = (i: number, entered: string) => {
    const n = Number(entered);
    const next = rows.slice();
    next[i] = {
      ...next[i],
      widthMeters: Number.isFinite(n)
        ? roundLengthForStorage(convertLength(n, lengthUnit, LengthUnit.Meter))
        : next[i].widthMeters,
    };
    onChange(next);
  };

  const setDate = (i: number, local: string) => {
    const d = new Date(local);
    if (isNaN(d.getTime())) return;
    const next = rows.slice();
    next[i] = { ...next[i], effectiveFrom: d.toISOString() };
    onChange(next);
  };

  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));

  /**
   * A header change from today onwards — the machine actually got a different
   * header. Never edits an existing line, so no past day moves.
   */
  const addNew = () => {
    const last = rows[rows.length - 1];
    const lastT = last ? new Date(last.effectiveFrom).getTime() : 0;
    // Strictly after the newest line so the two cannot collide on one instant,
    // and never meaningfully ahead of now (the API rejects a future start).
    const when = new Date(Math.max(Date.now(), lastT + 1000));
    onChange([
      ...rows,
      { effectiveFrom: when.toISOString(), widthMeters: last?.widthMeters ?? 6.7 },
    ]);
  };

  const addEarlier = () => {
    // Halfway between the first two rows, or a month before the only one, so
    // the new row lands somewhere plausible and always in the past.
    const first = rows[0];
    const second = rows[1];
    const anchor = new Date(second?.effectiveFrom ?? Date.now()).getTime();
    const start = new Date(first?.effectiveFrom ?? Date.now()).getTime();
    const when = second
      ? new Date((anchor + start) / 2)
      : new Date(Date.now() - 30 * 24 * 3600 * 1000);
    onChange(
      [
        ...rows,
        { effectiveFrom: when.toISOString(), widthMeters: first?.widthMeters ?? 6.7 },
      ].sort(
        (a, b) =>
          new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime()
      )
    );
  };

  return (
    <div>
      <Warning>{labels.restatesWarning}</Warning>
      {rows.map((r, i) => (
        <Block key={`${r.effectiveFrom}-${i}`}>
          <RowLabel>
            <Text size={12} color="#899093">
              {i === 0 ? labels.fromTheStart : labels.changedOn}
            </Text>
            {i > 0 && !disabled && (
              <RemoveBtn type="button" onClick={() => remove(i)}>
                {labels.remove}
              </RemoveBtn>
            )}
          </RowLabel>
          <Row alignItems="center" style={{ gap: 8 }}>
            {i > 0 && (
              <Field
                type="datetime-local"
                disabled={disabled}
                value={toLocalInput(r.effectiveFrom)}
                onChange={(e) => setDate(i, e.target.value)}
              />
            )}
            <Field
              type="number"
              disabled={disabled}
              value={display(r.widthMeters)}
              onChange={(e) => setWidth(i, e.target.value)}
              style={{ maxWidth: i > 0 ? 110 : undefined }}
            />
            <Text size={13} color="#899093">
              {unitLabel}
            </Text>
          </Row>
        </Block>
      ))}
      {!disabled && labels.addNew != null && (
        <>
          <AddBtn type="button" onClick={addNew}>
            {labels.addNew}
          </AddBtn>
          <SizedBox height={8} />
        </>
      )}
      {!disabled && (
        <AddBtn type="button" onClick={addEarlier}>
          {labels.addEarlier}
        </AddBtn>
      )}
    </div>
  );
};

export default WidthHistoryEditor;
